import {Component, OnInit, OnDestroy} from '@angular/core';
import {NotificationService} from '@services/notification.service';
import {Subscription} from 'rxjs';
@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {
    visible: boolean = false;
    notifications: any[] = [];
    notificationCount: number = 0;
    pollingSubscription: Subscription;
    userDetail: any;
    notificationsCleared: boolean = false;
    constructor(private notificationService: NotificationService) {}
    ngOnInit() {
        const pollingInterval = 30000;
        this.userDetail = JSON.parse(localStorage.getItem('UserDetail'));

        this.pollingSubscription = this.notificationService
            .pollNotifications(this.userDetail.id, pollingInterval)
            .subscribe({
                next: (data) => {
                    const notifications = data.farmhouseNotifications || [];
                    this.notificationsCleared = false;
                    this.notifications = notifications.slice(0, 5);
                    this.notificationCount = notifications.length;
                },
                error: () => {}
            });
    }
    // Check if the new notifications are different from the current ones
    private hasNotificationsChanged(newNotifications: any[]): boolean {
        if (this.notifications.length !== newNotifications.length) {
            return true; // Different count of notifications
        }

        // Compare each notification
        for (let i = 0; i < this.notifications.length; i++) {
            if (
                this.notifications[i].message !== newNotifications[i].message ||
                this.notifications[i].timeAgo !== newNotifications[i].timeAgo
            ) {
                return true; // Content difference found
            }
        }
        return false; // No differences found
    }
    closeDropdown() {
        this.visible = !this.visible;
        this.notificationCount = 0; // Reset the count
        this.notifications = [];
        this.notificationsCleared = true;
    }
    closeDropdownmain() {
        this.visible = true;
    }
    ngOnDestroy(): void {
        // Unsubscribe from the polling subscription to prevent memory leaks
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
        }
    }
    calculateTimeAgo(dateTime: string): string {
        const notificationDate = new Date(dateTime);
        const now = new Date();
        const diffInMinutes = Math.floor(
            (now.getTime() - notificationDate.getTime()) / (1000 * 60)
        );

        if (diffInMinutes < 60) {
            return `${diffInMinutes} mins ago`;
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else {
            const days = Math.floor(diffInMinutes / 1440);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }
    }

    getNotificationIcon(notificationType: string): string {
        const iconMap = {
            temperature: 'assets/img/notification-icons/temperature.svg',
            water: 'assets/img/notification-icons/water.svg',
            camera: 'assets/img/notification-icons/camera.svg',
            default: 'assets/img/notification-icons/default.svg'
        };
        // Extract notification type from title since sensorName is empty
        const actualType = this.getNotificationTypeFromTitle(notificationType);
        return iconMap[actualType] || iconMap['default'];
    }

    getNotificationTypeFromTitle(notification: any): string {
        if (notification?.title) {
            const title = notification.title.toLowerCase();
            if (title.includes('humidity')) return 'humidity';
            if (title.includes('temperature')) return 'temperature';
            if (title.includes('water')) return 'water';
            if (title.includes('camera')) return 'camera';
            if (title.includes('mortality')) return 'mortality';
            if (title.includes('feed')) return 'feed';
        }
        return 'default';
    }
    getIconClass(notificationType: string): string {
        const iconClassMap = {
            temperature: 'icon-temperature',
            humidity: 'icon-humidity',
            water: 'icon-water',
            camera: 'icon-camera',
            mortality: 'icon-mortality',
            feed: 'icon-feed',
            default: 'icon-default'
        };
        return iconClassMap[notificationType] || iconClassMap['default'];
    }

    getPriorityClass(priority: string): string {
        const priorityClass = {
            High: 'priority-high',
            Medium: 'priority-medium',
            Low: 'priority-low'
        };
        return priorityClass[priority] || 'priority-medium';
    }

    getNotificationTitle(notification: any): string {
        if (notification.title) {
            // Extract title from HTML like "<h3>Humidity Reading <span>High</span> (20)</h3>"
            let cleanTitle = notification.title
                .replace(/<h3>/g, '')
                .replace(/<\/h3>/g, '')
                .replace(/<span>/g, '')
                .replace(/<\/span>/g, '')
                .replace(/\([^)]*\)/g, '') // Remove anything in parentheses like (20)
                .trim();
            return cleanTitle;
        }

        const titleMap = {
            temperature: 'Temperature Spike',
            humidity: 'Humidity Reading',
            water: 'Water Flow Irregularity',
            camera: 'Camera AI Anomaly',
            mortality: 'Mortality Alert',
            feed: 'Feed Stock Low',
            default: 'Humidity Reading'
        };
        return titleMap[notification.sensorName] || titleMap['default'];
    }

    // Process notification message to extract clean description
    getProcessedMessage(notification: any): string {
        if (notification.description) {
            // Extract location and time info from description
            let cleanMessage = notification.description
                .replace(/<h3>.*?<\/h3>/g, '') // Remove h3 tags and content
                .replace(/<h5>/g, '')
                .replace(/<\/h5>/g, '')
                .replace(/<p>/g, '')
                .replace(/<\/p>/g, '')
                .replace(/<b>/g, '')
                .replace(/<\/b>/g, '')
                .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                .trim();
            return cleanMessage;
        }

        if (notification.message) {
            // Fallback to message field if description doesn't exist
            let cleanMessage = notification.message
                .replace(/<[^>]*>/g, '') // Remove all HTML tags
                .replace(/\([^)]*\)/g, '') // Remove anything in parentheses
                .trim();
            return cleanMessage;
        }

        return '';
    }
}
